// Client mirror of the Stew tool registry. The authoritative copy lives in
// supabase/functions/manual-assistant/index.ts. Keys are machinery: they are
// compared and used for routing. Labels are display only. If the edge function
// ever emits a key the client does not know, nothing renders (guardrail).

import {
  Building2,
  Users,
  Award,
  Activity,
  ListChecks,
  Archive,
  ShieldCheck,
  MessageSquare,
  BookOpen,
  Compass,
  HandHeart,
  UserMinus,
  UserPlus,
  ScrollText,
  Home,
  type LucideIcon,
} from "lucide-react";

export type StewToolKey =
  | "form_a_company"
  | "offices"
  | "officer_certificates"
  | "governance_health"
  | "tasks"
  | "the_record"
  | "succession"
  | "give_into_stewardship"
  | "conclude_membership"
  | "appoint_officer"
  | "execute_religious_covenant"
  | "establish_center_occupancy"
  | "ask_a_person"
  | "library"
  | "my_journey";

export interface StewTool {
  key: StewToolKey;
  label: string;
  route: string;
  icon: LucideIcon;
}

export const STEW_TOOL_REGISTRY: Record<StewToolKey, StewTool> = {
  form_a_company: {
    key: "form_a_company",
    label: "Form a Company",
    route: "/governance/form-a-company",
    icon: Building2,
  },
  offices: {
    key: "offices",
    label: "Offices",
    route: "/governance/offices",
    icon: Users,
  },
  officer_certificates: {
    key: "officer_certificates",
    label: "Officer's Certificates",
    route: "/governance/certificates",
    icon: Award,
  },
  governance_health: {
    key: "governance_health",
    label: "Governance Health",
    route: "/governance/health",
    icon: Activity,
  },
  tasks: {
    key: "tasks",
    label: "Tasks",
    route: "/governance/tasks",
    icon: ListChecks,
  },
  the_record: {
    key: "the_record",
    label: "The Record",
    route: "/governance/record",
    icon: Archive,
  },
  succession: {
    key: "succession",
    label: "Succession",
    route: "/succession/overview",
    icon: ShieldCheck,
  },
  give_into_stewardship: {
    key: "give_into_stewardship",
    label: "Give Something Into Stewardship",
    route: "/workflows?workflow=give_into_stewardship",
    icon: HandHeart,
  },
  conclude_membership: {
    key: "conclude_membership",
    label: "Conclude a Membership",
    route: "/workflows?workflow=conclude_membership",
    icon: UserMinus,
  },
  appoint_officer: {
    key: "appoint_officer",
    label: "Appoint an Officer",
    route: "/workflows?workflow=appoint_officer",
    icon: UserPlus,
  },
  execute_religious_covenant: {
    key: "execute_religious_covenant",
    label: "Execute the Religious Covenant",
    route: "/workflows?workflow=execute_religious_covenant",
    icon: ScrollText,
  },
  establish_center_occupancy: {
    key: "establish_center_occupancy",
    label: "Establish the Center and Occupancy",
    route: "/workflows?workflow=establish_center_occupancy",
    icon: Home,
  },
  ask_a_person: {
    key: "ask_a_person",
    label: "Ask a person",
    route: "/support/ask",
    icon: MessageSquare,
  },
  library: {
    key: "library",
    label: "Library",
    route: "/support/library",
    icon: BookOpen,
  },
  my_journey: {
    key: "my_journey",
    label: "Finish setup",
    route: "/journey",
    icon: Compass,
  },
};

const TOOLS_LINE_RE = /\n?\s*TOOLS:\s*(\[[^\]\n]*\])\s*$/m;

/**
 * Extract the TOOLS: [...] delimiter block from a raw assistant reply. Returns
 * the cleaned text (block removed) and the parsed list of known tool keys,
 * capped at two. Unknown keys are dropped silently.
 */
export function parseStewTools(raw: string): {
  text: string;
  tools: StewToolKey[];
} {
  if (!raw) return { text: raw || "", tools: [] };
  const match = raw.match(TOOLS_LINE_RE);
  if (!match) return { text: raw, tools: [] };
  let keys: string[] = [];
  try {
    const parsed = JSON.parse(match[1]);
    if (Array.isArray(parsed)) {
      keys = parsed.filter((k) => typeof k === "string");
    }
  } catch {
    keys = [];
  }
  const known = keys
    .filter((k): k is StewToolKey => k in STEW_TOOL_REGISTRY)
    .slice(0, 2);
  const text = raw.replace(TOOLS_LINE_RE, "").trimEnd();
  return { text, tools: known };
}
